import { useEffect, useState } from "react";
import { BadgePercent, Banknote, CheckCircle2, Clock4, Wallet } from "lucide-react";
import { money } from "@/lib/rides";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const PAY_LABEL = { pay_at_pickup: "Pay at Pickup", ride_credit: "Scoopz Ride Credit", post_payment: "Post-Payment" };
export const PP_LABEL = { approved: "Approved", pending: "Pending review", declined: "Declined", suspended: "Suspended" };
const Title = ({ children }) => <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{children}</p>;

function DiscountField({ opts, setOpts, co }) {
  const [code, setCode] = useState(opts.discountCode);
  useEffect(() => { setCode(opts.discountCode); }, [opts.discountCode]);
  const applied = opts.discountCode && co?.discount;
  return (
    <div className="rounded-2xl border bg-card p-4" data-testid="discount-section">
      <Title>Discount code</Title>
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); setOpts({ ...opts, discountCode: code.trim().toUpperCase() }); }}>
        <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter code" className="h-10 rounded-full uppercase" data-testid="discount-code-input" />
        {opts.discountCode ? (
          <Button key="remove" type="button" variant="outline" className="h-10 rounded-full" onClick={() => setOpts({ ...opts, discountCode: "" })} data-testid="discount-remove-button">Remove</Button>
        ) : (
          <Button key="apply" type="submit" className="h-10 rounded-full" disabled={!code.trim()} data-testid="discount-apply-button">Apply</Button>
        )}
      </form>
      {applied && <p className="mt-2 flex items-center gap-1 text-xs font-semibold text-neon-lime" data-testid="discount-applied"><CheckCircle2 className="h-3.5 w-3.5" /> {co.discount.code} applied · -{money(co.discount.amount)}{co.discount.replaces_recurring ? " (instead of your recurring discount)" : ""}</p>}
      {opts.discountCode && co?.discount_error && <p className="mt-2 text-xs font-semibold text-destructive" data-testid="discount-error">{co.discount_error}</p>}
      {opts.discountCode && co?.discount_conflict && <p className="mt-2 text-xs font-semibold text-amber-300" data-testid="discount-conflict">{co.discount_conflict}</p>}
    </div>
  );
}

const Option = ({ on, onClick, icon: Icon, title, sub, testid, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
    className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-[border-color,background-color] disabled:cursor-not-allowed disabled:opacity-40 ${on ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/30"}`}>
    <Icon className={`h-5 w-5 shrink-0 ${on ? "text-primary" : "text-muted-foreground"}`} />
    <span className="min-w-0"><span className="block text-sm font-semibold">{title}</span><span className="block text-xs text-muted-foreground">{sub}</span></span>
  </button>
);

function PaymentOptions({ opts, setOpts, co, allowPost = true }) {
  const set = (patch) => setOpts({ ...opts, ...patch });
  const pp = co?.post_payment_status;
  const ppBlocked = pp === "declined" || pp === "suspended";
  const credit = co?.credit_available ?? 0;
  return (
    <div className="space-y-2 rounded-2xl border bg-card p-4" data-testid="payment-section">
      <Title>Payment method</Title>
      <Option on={opts.payMethod === "pay_at_pickup"} onClick={() => set({ payMethod: "pay_at_pickup" })} icon={Banknote} title="Pay at Pickup" sub="Pay your driver when you're picked up" testid="pay-option-pickup" />
      <Option on={opts.payMethod === "ride_credit"} onClick={() => set({ payMethod: "ride_credit" })} disabled={credit <= 0} icon={Wallet}
        title="Prepay with Ride Credit" sub={`${money(credit)} available`} testid="pay-option-credit" />
      {allowPost && <Option on={opts.payMethod === "post_payment"} onClick={() => set({ payMethod: "post_payment" })} disabled={ppBlocked} icon={Clock4}
        title={pp === "approved" ? "Post-Payment" : "Apply for Post-Payment"} sub={pp ? `Post-Payment: ${PP_LABEL[pp]}` : "Subject to Scoopz Admin approval, not automatic"} testid="pay-option-post" />}
      {opts.payMethod === "ride_credit" && co?.amount_due > 0 && (
        <div className="rounded-xl bg-muted p-3 text-xs" data-testid="credit-remainder">
          <p className="mb-2 font-semibold">Remaining {money(co.amount_due)} — pay with:</p>
          <div className="flex gap-2">
            {["pay_at_pickup", "post_payment"].map((m) => (
              <button key={m} type="button" disabled={m === "post_payment" && pp !== "approved"} onClick={() => set({ payRemainder: m })} data-testid={`remainder-${m}`}
                className={`rounded-full border px-3 py-1 font-semibold disabled:opacity-40 ${opts.payRemainder === m ? "border-primary bg-primary text-primary-foreground" : "border-white/15"}`}>{PAY_LABEL[m]}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export const Line = ({ label, value, testid, strong }) => (
  <div className={`flex justify-between gap-3 ${strong ? "text-base font-bold" : "text-sm"}`}>
    <span className={strong ? "" : "text-muted-foreground"}>{label}</span><span className="shrink-0 font-semibold" data-testid={testid}>{value}</span>
  </div>
);

export function FareSummary({ quote, co, method }) {
  const n = co.fares.length;
  const per = (v) => (n > 1 ? `${money(v)} × ${n}` : money(v));
  const pp = co.post_payment_status;
  const methodLabel = method === "post_payment" ? `Post-Payment — ${pp === "approved" ? "Approved" : "Pending review"}`
    : method === "ride_credit" ? `Scoopz Ride Credit${co.amount_due > 0 ? ` + ${PAY_LABEL[co.remaining_method] || "—"}` : ""}` : "Pay at Pickup";
  return (
    <div className="space-y-1.5 rounded-2xl border border-primary/40 bg-card p-4" data-testid="fare-summary">
      <Title>Final fare</Title>
      <Line label="Base / GPS fare" value={per(cents2(quote.mileage_subtotal + quote.base_fee))} testid="summary-gps-fare" />
      <Line label="Service window (wait time)" value={`+${per(quote.wait_total)}`} testid="summary-service-window" />
      <Line label="Airport component" value={`+${per(quote.airport.amount)}`} testid="summary-airport" />
      {quote.location_adjustment?.applied && <Line label={`Special location (${quote.location_adjustment.name})`} value={`${quote.location_adjustment.amount < 0 ? "-" : "+"}${per(Math.abs(quote.location_adjustment.amount))}`} testid="summary-location-adjustment" />}
      {co.recurring_discount > 0 && <Line label="Recurring discount" value={`-${money(co.recurring_discount)}`} testid="summary-recurring" />}
      <Line label="Subtotal" value={money(co.subtotal)} testid="summary-subtotal" />
      <Line label={co.discount ? `Discount (${co.discount.code})` : "Discount"} value={`-${money(co.discount?.amount || 0)}`} testid="summary-discount" />
      <Line label="Booking fee" value="$0.00" testid="summary-booking-fee" />
      <div className="border-t pt-2"><Line strong label="TOTAL" value={money(co.total)} testid="summary-total" /></div>
      {co.credit_applied > 0 && <Line label="Ride Credit applied" value={`-${money(co.credit_applied)}`} testid="summary-credit" />}
      <Line strong label={method === "pay_at_pickup" ? "PAYMENT DUE AT PICKUP" : "Amount due"} value={money(co.amount_due)} testid="summary-amount-due" />
      <p className="pt-1 text-xs text-muted-foreground" data-testid="summary-payment-method">Payment method: {methodLabel}</p>
    </div>
  );
}
const cents2 = (v) => Math.round(v * 100) / 100;

export function CheckoutPanel({ quote, opts, setOpts, allowPost = true, summary = true }) {
  const co = quote?.checkout?.ok ? quote.checkout : null;
  return (
    <>
      <DiscountField opts={opts} setOpts={setOpts} co={co} />
      <PaymentOptions opts={opts} setOpts={setOpts} co={co} allowPost={allowPost} />
      {co && summary && <FareSummary quote={quote} co={co} method={opts.payMethod} />}
      {co?.errors?.length > 0 && <p className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="checkout-error">{co.errors[0]}</p>}
    </>
  );
}

export const BadgeIcon = BadgePercent;
